import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import React, { FC, useEffect, useState } from 'react';

import { ProviderCredential } from '~/shared/services/userService';

import {
  appleDisplayName,
  isAppleUserCancel,
  loadAppleIdSdk,
} from '../utils/appleId';
import { generateNonce, sha256Hex } from '../utils/nonce';

const LABELS = {
  signin_with: 'Sign in with Apple',
  signup_with: 'Sign up with Apple',
  continue_with: 'Continue with Apple',
} as const;

type Props = {
  onCredential: (credential: ProviderCredential) => void;
  onError?: () => void;
  disabled?: boolean;
  text?: keyof typeof LABELS;
  /**
   * Runs before the Apple popup opens (e.g. validate form fields). Return
   * false to abort silently. Kept synchronous-ish so the popup still counts
   * as user-initiated for popup blockers.
   */
  beforeSignIn?: () => boolean | Promise<boolean>;
};

// Custom "Sign in with Apple" button in Apple's "white with outline" style
// (white fill, 1px outline, ≥ 30pt tall, 4px radius, logo + label), which is
// the variant Apple allows on light *and* dark surfaces — the same choice
// Google Identity Services makes with its "outline" theme, so the two buttons
// stay a matched pair in both themes instead of one flipping to black.
// Geometry and type mirror the GSI button rendered above it: 40px tall,
// #dadce0 outline, #3c4043 ink, 14px/500 label, logo pinned left with the
// label centred in the remainder.
// https://developer.apple.com/design/human-interface-guidelines/sign-in-with-apple
const useStyles = makeStyles({
  root: {
    position: 'relative',
    height: 40,
    // GSI: 12px edge + logo + gap on the left, 12px on the right; the label
    // is centred in what remains, so it sits where Google's does (measured
    // side by side: label centres within 1px).
    padding: '0 12px 0 38px',
    backgroundColor: '#fff',
    color: '#3c4043',
    border: '1px solid #dadce0',
    textTransform: 'none',
    // Same type spec as the GSI label ("Google Sans", arial, sans-serif /
    // 14px / 500 / 0.25px). Google Sans is never installed locally, so both
    // buttons resolve to Arial — identical glyphs and stroke weight instead
    // of the system font's heavier medium next to Google's regular.
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0.25,
    fontFamily: 'arial, sans-serif',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#f7f8f8',
      borderColor: '#d2e3fc',
    },
    '&$disabled': {
      backgroundColor: '#fff',
      color: '#3c4043',
      opacity: 0.6,
    },
  },
  disabled: {},
  logo: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 18,
    height: 18,
    fill: 'currentColor',
  },
});

const AppleLogo: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

/**
 * Sign in with Apple (web) via Sign in with Apple JS in popup mode.
 *
 * Per attempt: a fresh nonce is generated — its SHA-256 goes to Apple (ends up
 * as the ID token's `nonce` claim), the raw value goes to our API, exactly
 * like the Google button. A random `state` is echoed back by Apple and checked
 * before the credential is trusted. Apple only returns the user's name on the
 * very first authorization; it is forwarded as `credential.name`.
 *
 * `redirectURI` must be one of the Return URLs registered on the Services ID
 * even in popup mode; the current page URL (origin + path) is used so each
 * auth page only needs its own URL registered.
 */
const AppleButton: FC<Props> = ({
  onCredential,
  onError,
  disabled = false,
  text = 'signin_with',
  beforeSignIn,
}) => {
  const classes = useStyles();
  const [busy, setBusy] = useState(false);

  // Warm the SDK as soon as the button is on screen so the click → popup path
  // stays well inside the browser's user-activation window (popup blockers).
  // Failures are swallowed here; the click handler retries and reports them.
  useEffect(() => {
    loadAppleIdSdk().catch(() => undefined);
  }, []);

  const handleClick = async () => {
    if (beforeSignIn && !(await beforeSignIn())) {
      return;
    }

    setBusy(true);
    try {
      const nonce = generateNonce();
      const state = generateNonce();
      const [hashedNonce, AppleID] = await Promise.all([
        sha256Hex(nonce),
        loadAppleIdSdk(),
      ]);

      AppleID.auth.init({
        clientId: CONFIG.appleClientId,
        scope: 'name email',
        redirectURI: `${window.location.origin}${window.location.pathname}`,
        state,
        nonce: hashedNonce,
        usePopup: true,
      });

      const response = await AppleID.auth.signIn();
      if (response.authorization.state !== state) {
        throw new Error('Sign in with Apple state mismatch');
      }

      onCredential({
        idToken: response.authorization.id_token,
        nonce,
        name: appleDisplayName(response.user),
      });
    } catch (error) {
      if (!isAppleUserCancel(error)) {
        onError?.();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      fullWidth
      variant="contained"
      disableElevation
      classes={{ root: classes.root, disabled: classes.disabled }}
      disabled={disabled || busy}
      aria-busy={busy}
      onClick={handleClick}
      data-testid="apple-login"
    >
      <AppleLogo className={classes.logo} />
      {LABELS[text]}
    </Button>
  );
};

export default AppleButton;
